import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import DefaultLayout from './layouts/DefaultLayout';
import AdminLayout from './layouts/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';
import AuthInitializer from './components/AuthInitializer';
import Home from './pages/Home/Home';
import About from './pages/About/About';
import Tracking from './pages/Tracking';
import Careers from './pages/Careers';
import Login from './pages/Login';
import Register from './pages/Register';
import TermsAndConditions from './pages/TermsAndConditions';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import ChangePassword from './pages/ChangePassword';
import BankAccountManagement from './pages/BankAccountManagement';
import Users from './pages/Users/Users';
import Clients from './pages/Clients/Clients';
import JobApplicants from './pages/JobApplicants/JobApplicants';
import JobPostings from './pages/JobPostings';
import Branches from './pages/Branches/Branches';
import Consignments, { ConsignmentCreate } from './pages/Consignments';
import Cities from './pages/Cities/Cities';
import Logs from './pages/Logs/Logs';
import Vehicles from './pages/Vehicles/Vehicles';
import Riders from './pages/Riders/Riders';
import { MasterUnits, MasterUnitCreate, MasterUnitView, MasterUnitEdit } from './pages/MasterUnits';
import { InboundMasterUnits, InboundMasterUnitView } from './pages/InboundMasterUnits';
import { ScanConsignments, ScanPickupSheetConsignments, ScanMasterUnitConsignments } from './pages/ScanConsignments';
import PickupSheets from './pages/PickupSheets/PickupSheets';
import PickupSheetView from './pages/PickupSheets/PickupSheetView';
import { DeliverySheets, DeliverySheetCreate, DeliverySheetEdit, DeliverySheetView, FailedConsignments } from './pages/DeliverySheets';
import { RunSheets, RunSheetCreate, RunSheetView } from './pages/RunSheets';
import PlaceholderPage from './pages/PlaceholderPage';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import CustomerFinances from './pages/CustomerFinances';
import ConsignmentPricing from './pages/CustomerFinances/ConsignmentPricing';
import BranchFinances from './pages/BranchFinances';
import { HeadOfficeFinances } from './pages/HeadOfficeFinances';
import { GeneralSettings, DatabaseBackups, TariffCard, AdminActions } from './pages/Settings';

function AppContent() {
    const { theme } = useTheme();

    return (
        <MuiThemeProvider theme={theme}>
            <CssBaseline />
            <AuthInitializer />
            <Router>
                <Routes>
                    {/* Default Layout Routes */}
                    <Route element={<DefaultLayout />}>
                        <Route path="/" element={<Home />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/careers" element={<Careers />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/tracking" element={<Tracking />} />
                        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                    </Route>

                    {/* Admin Layout Routes */}
                    <Route
                        element={
                            <ProtectedRoute>
                                <AdminLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/change-password" element={<ChangePassword />} />
                        <Route path="/bank-accounts" element={<BankAccountManagement />} />
                        <Route path="/users" element={<Users />} />
                        <Route path="/clients" element={<Clients />} />
                        <Route path="/job-postings" element={<JobPostings />} />
                        <Route path="/job-applicants" element={<JobApplicants />} />
                        <Route path="/branches" element={<Branches />} />
                        <Route path="/consignments" element={<Consignments />} />
                        <Route path="/consignments/create" element={<ConsignmentCreate />} />
                        <Route path="/cities" element={<Cities />} />
                        <Route path="/logs" element={<Logs />} />

                        {/* Master Unit Routes */}
                        <Route path="/master-units" element={<MasterUnits />} />
                        <Route path="/master-units/create" element={<MasterUnitCreate />} />
                        <Route path="/master-units/:id" element={<MasterUnitView />} />
                        <Route path="/master-units/:id/edit" element={<MasterUnitEdit />} />

                        {/* Inbound Master Unit Routes */}
                        <Route path="/inbound-master-units" element={<InboundMasterUnits />} />
                        <Route path="/inbound-master-units/:id" element={<InboundMasterUnitView />} />

                        {/* Pickup Sheets Routes */}
                        <Route path="/pickup-sheets" element={<PickupSheets />} />
                        <Route path="/pickup-sheets/:id" element={<PickupSheetView />} />

                        {/* Scan Consignments Routes */}
                        <Route path="/consignment-scan" element={<ScanConsignments />} />
                        <Route path="/pickup-sheet-scan" element={<ScanPickupSheetConsignments />} />
                        <Route path="/master-unit-scan" element={<ScanMasterUnitConsignments />} />

                        {/* Delivery Sheets Routes */}
                        <Route path="/delivery-sheets" element={<DeliverySheets />} />
                        <Route path="/delivery-sheets/create" element={<DeliverySheetCreate />} />
                        <Route path="/delivery-sheets/:id/edit" element={<DeliverySheetEdit />} />
                        <Route path="/delivery-sheets/:id" element={<DeliverySheetView />} />
                        <Route path="/delivery-sheet-failed" element={<FailedConsignments />} />

                        {/* Run Sheets Routes */}
                        <Route path="/run-sheets" element={<RunSheets />} />
                        <Route path="/run-sheets/create" element={<RunSheetCreate />} />
                        <Route path="/run-sheets/:id" element={<RunSheetView />} />

                        {/* Shipments Routes */}
                        <Route path="/shipments" element={<PlaceholderPage title="All Shipments" description="View and manage all your shipments." />} />
                        <Route path="/shipments/create" element={<PlaceholderPage title="Create Shipment" description="Create a new shipment." />} />
                        <Route path="/shipments/track" element={<PlaceholderPage title="Track Shipment" description="Track shipment status and location." />} />
                        <Route path="/shipments/schedule" element={<PlaceholderPage title="Schedule Pickup" description="Schedule pickup for shipments." />} />

                        {/* Analytics Routes */}
                        <Route path="/reports" element={<PlaceholderPage title="Reports" description="View analytics and generate reports." />} />
                        <Route path="/analytics/performance" element={<PlaceholderPage title="Performance Analytics" description="View performance metrics and KPIs." />} />
                        <Route path="/analytics/revenue" element={<PlaceholderPage title="Revenue Analytics" description="Track revenue and financial metrics." />} />

                        {/* Management Routes */}
                        <Route path="/customers" element={<PlaceholderPage title="Customers" description="Manage customer information and relationships." />} />
                        <Route path="/drivers" element={<PlaceholderPage title="Drivers" description="Manage driver information and assignments." />} />
                        <Route path="/vehicles" element={<Vehicles />} />
                        <Route path="/riders" element={<Riders />} />

                        {/* Support Routes */}
                        <Route path="/support" element={<PlaceholderPage title="Help Center" description="Get help and support for the platform." />} />
                        <Route path="/contact" element={<PlaceholderPage title="Contact Us" description="Contact our support team." />} />

                        {/* Settings Routes */}
                        <Route path="/settings/general" element={<GeneralSettings />} />
                        <Route path="/settings/backup" element={<DatabaseBackups />} />
                        <Route path="/settings/tariff" element={<TariffCard />} />
                        <Route path="/settings/admin-actions" element={<AdminActions />} />
                        <Route path="/consignment-pricing" element={<ConsignmentPricing />} />
                        <Route path="/customer-finances" element={<CustomerFinances />} />
                        <Route path="/payments" element={<CustomerFinances />} />
                        <Route path="/new-payment" element={<CustomerFinances />} />
                        <Route path="/debit-credit-notes" element={<CustomerFinances />} />
                        <Route path="/invoices" element={<CustomerFinances />} />
                        <Route path="/ledger" element={<CustomerFinances />} />
                        <Route path="/branch-finances" element={<BranchFinances />} />
                        <Route path="/branch-ledger" element={<BranchFinances />} />
                        <Route path="/head-office-finances" element={<HeadOfficeFinances />} />
                    </Route>

                    {/* Redirect /admin to /dashboard for backward compatibility */}
                    <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
                </Routes>
            </Router>
        </MuiThemeProvider>
    );
}

function App() {
    return (
        <Provider store={store}>
            <ThemeProvider>
                <AppContent />
            </ThemeProvider>
        </Provider>
    );
}

export default App;
